import {SubAPI} from "./SubAPI";
import {Paginated} from "./Users";
import {Div10Spec} from "./Pricing";
import {DoorSpec, FrameSpec, Product} from "./Products";
import {ScreenElevationCompat} from "../pages/project/quote/openings/ScreenElevationPopup";
import {PrepDetail} from "./QuoteHardware";
import {DateString} from "./Projects";
import {Opening} from "./Openings";

export class ProjectFreeformChange extends SubAPI {
    list(props: {
        project: number;
        offset?: number
    }) {
        return this.fetcher.get<Paginated<FreeformChange>>("/api/project-freeform-change/list", props);
    }

    get(props: {
        id: number;
    }) {
        return this.fetcher.get<FreeformChange>("/api/project-freeform-change", props);
    }

    upsert(input: Partial<FreeformChange>) {
        return this.fetcher.post<FreeformChange>("/api/project-freeform-change", input);
    }

    addItemsFromRoute(props: {
        freeformChange: number;
        routeStopGroup: number;
        inventoryRequests: number[];
    }) {
        return this.fetcher.post<{}>("/api/project-freeform-change/add-items-for-route", props)
    }

    upsertItem(input: Partial<FreeformChangeItem>) {
        return this.fetcher.post("/api/project-freeform-change/item", input);
    }

    listItemsWithProducts(props: {
        change: number;
    }) {
        return this.fetcher.get<FreeformChangeItem[]>("/api/project-freeform-change/with-products", props);
    }
    
    listProductsByType(props: {
        change: number;
        kind: "hardware" | "door" | "frame" | "div10"
    }) {
        return this.fetcher.get<ChildProductLine[]>("/api/project-freeform-change/products", props);
    }

    getSpecDetails(props: {
        id: number;
    } | {
        opening: number;
        change: number;
    }) {
        return this.fetcher.get<ChangeDetail>("/api/project-freeform-change/item/detailed", props);
    }

    addSpecProducts(props: ChangeDetail | Pick<ChangeDetail, "change"|"returnItems"|"div10Items"|"mode"|"standaloneHardware">) {
        return this.fetcher.post<AddResult>("/api/project-freeform-change/item/detailed", props)
    }

    addSpecProductsPreview(props: ChangeDetail | Pick<ChangeDetail, "change"|"returnItems"|"div10Items"|"mode"|"standaloneHardware">) {
        return this.fetcher.post<PreviewResult>("/api/project-freeform-change/item/detailed/preview", props)
    }

    addBulkSpecProducts(props: {
        change: number;
        addItems: SpecAddItem[];
        changes: OpeningChange[];
        openingPart: "active" | "inactive" | "both";
        openings: number[];
        bulkHardwareChanges: {
            fromProduct: number;
            remove?: boolean;
            replaceWith?: number;
            customerSupplied?: boolean;
        }[]
        bulkAddHardware: {
            product: number;
            activeQty: number;
            inactiveQty: number;
            commonQty: number;
            customerSupplied?: boolean;
        }[]
    }) {
        return this.fetcher.post<{task: string}>("/api/project-freeform-change/item/bulk-opening-changes/with-task", props)
    }

    addSpecProductsBulk(props: (
        ChangeDetail |
        Pick<ChangeDetail, "change"|"returnItems"|"mode"|"standaloneHardware"> |
        Pick<ChangeDetail, "change"|"mode"|"returnItems"|"qty"|"project">
    )[]) {
        return this.fetcher.post("/api/project-freeform-change/item/detailed-bulk", props)
    }

    saveCustomerSummary(props: {
        freeformChange: number;
        title: string;
        sections: Section[];
        notes: string;
    }) {
        return this.fetcher.post("/api/project-freeform-change/customer-summary", props)
    }

    getCustomerSummary(props: {
        freeformChange: number;
    }) {
        return this.fetcher.get<{
            id: number;
            freeformChange: number;
            sections: Section[]
            notes: string;
            title: string;
        } | null>("/api/project-freeform-change/customer-summary", props)
    }

    listPrices(props: {
        change: number;
    }) {
        return this.fetcher.get<PriceItem[]>("/api/project-freeform-change/price-items", props)
    }

    upsertPrice(props: Partial<PriceItem>) {
        return this.fetcher.post("/api/project-freeform-change/price-item", props);
    }

    predictHardwareChanges(props: ChangeDetail) {
        return this.fetcher.post<{
            warnings: string[]
            items: HardwarePredictResult[];
        }>("/api/project-freeform-change/predict-hardware-changes", props);
    }

    listItemPrices(props: {
        change: number;
    }) {
        return this.fetcher.get<ItemPrice[]>("/api/project-freeform-change/item-prices", props)
    }

    upsertItemPrice(input: ItemPrice) {
        return this.fetcher.post("/api/project-freeform-change/item-price", input);
    }
}

export type ItemPrice = {
    id: number;

    change: number;
    item: number;

    openingName: string;
    name: string;
    changeType: string;

    qty: number;

    unitCostCents: number;
    markupPercent: number | null;
    markupCents: number | null;
    extendedPrice: number;

    refundKeepPercent: number;
    refundFullRefund: boolean;
    refundAmount: number;
    refunded: boolean;

    archived: boolean;
}

export type OpeningChange = {
    key: keyof Opening | string;
    value: any;
}

export type Section = {
    id: number;
    name: string;
    description: string;
    amountCents: number;
}

export type HardwarePredictResult = {
    id: number;
    action: "add" | "remove";
    qty: number;
    product: Product;
    openingSubtype: string;
    inventoryRequest: number | null;
    inventoryRequestStatus: string | null;
}

export type AddResult = {
    checkAffected: string[];
}

export type PreviewResult = {
    frame: PreviewChangeItem[] | null | undefined
    frameCompared: boolean;
    frameOrdered: boolean;

    activeDoor: PreviewChangeItem[] | null | undefined
    activeDoorCompared: boolean;
    activeDoorOrdered: boolean;

    inactiveDoor: PreviewChangeItem[] | null | undefined
    inactiveDoorCompared: boolean;
    inactiveDoorOrdered: boolean;

    openingIsPair: boolean;
}

export type PreviewChangeItem = {
    willBreakConsistency: boolean;
    name: string;
    before: string;
    after: string;
}

export type PriceItem = {
    id: number;
    change: number;
    name: string;
    amountCents: number;
    archived: boolean;

    projectWorkOrderItem: number | null;
    projectWorkOrderItemStatus: string | null;

    purchaseOrder: number | null;
    purchaseOrderNumber: string | null;
    purchaseOrderStatus: string | null;
}

export type ChangeType = "return" | "new" | "replace";

export type FreeformChangeItem = {
    id: number;

    change: number;
    openingName: string;
    opening: number;
    newOpeningName: string | null;
    project: number;

    name: string;
    qty: number;
    logisticsStatus: string;
    changeType: ChangeType;

    products: ChildProductLine[] | null;
}

export type ChildProductLine = {
    id: number;
    name: string;
    qty: number;
    changeType: ChangeType;
    logisticsStatus: string;
    displaySubmitWillArchive?: boolean;

    remanufacture: boolean;
    purchaseOrder: number | null;
    purchaseOrderNumber: string | null;

    // div 10
    div10: Div10Spec | null;
    room: string;
    category: string;
    manufacturer: number;
    manufacturerName: string;
    productCode: string;
    finish: string;
    dimensions: string;
    dimWidth: string;
    dimHeight: string;
    dimLength: string;

    frame: FrameSpec | null;
    door: DoorSpec | null;
}

export type FreeformChange = {
    id: number;
    project: number;
    projectName: string;
    projectNumber: number;
    description: string;
    status: "draft" | "submitted";
    financeStatus: "ok" | "needs-review" | "pending-return";
    updatedBy: number;
    updatedAt: string;
    archived: boolean;
}

export type StandaloneHardwareItem = {
    id: number; // fake id
    product: number;
    productObj: Product;
    productCode: string;
    name: string;
    category: string;
    updatedAt: DateString;
    manufacturer: number;
    manufacturerName: string;

    activeQty: number;

    dimLength: string;
    dimWidth: string;
    dimHeight: string;

    archived: boolean;

    handing: string;

    manualScriptInputs?: ManualScriptInput[];
}

export type ManualScriptInput = {
    name: string;
    value: string;
}

export type OpeningHardwareItem = {
    id: number; // fake id
    product: number;
    productCode: string;
    name: string;
    manufacturerName: string;
    activeQty: number;
    inactiveQty: number;
    commonQty: number;
    customerSupplied: boolean;
    productIsAutoQtyHinge: boolean;
    overrideHingeActiveQty: number | null;
    overrideHingeInActiveQty: number | null;
    hwPrepSingleActiveDoor: string;
    hwPrepSingleFrame: string
    hwPrepPairActiveDoor: string;
    hwPrepPairInactiveDoor: string
    hwPrepPairFrame: string;
    dimHeight: string;
    archived: boolean;
    updatedAt?: DateString; // faked
    ignoreForLogistics?: boolean;

    electricStrikeConfig: {
        activeLeaf?: boolean;
        inactiveLeaf?: boolean;
    };
}

export type Div10Item = Div10Spec & {
    id: number; // fake id
    qty: number;
    manufacturer: number;
    manufacturerName: string;
    room: string;
    note: string;
    dimWidth: string;
    dimHeight: string;
    dimLength: string;
}

export type SpecAddItem = "door" | "frame" | "hardware" | "frame-anchors";

export type ReturnItem =  {
    id: number; // inventory request
    name: string;
    productCode: string;
    opening: number;
    openingName: string;
    type: string;
    category: string;
    qty?: number;
}

export type ChangeDetail = {
    id: number;
    change: number;
    project: number;

    mode: "replace" | "new-hardware" | "new-opening" | "new-anchors"
        | "new-wr-accessories" | "new-div10"
        | "new-specd" | "return";

    linkedOpening?: number;
    linkedOpeningName?: string;

    swapNameWithOpening?: number;
    swapNameWithOpeningName?: string;

    returnItems: ReturnItem[];

    standaloneHardware: StandaloneHardwareItem[];
    openingHardware: OpeningHardwareItem[];
    div10Items?: Div10Item[];

    // new-specd items
    addItems: SpecAddItem[];
    openingType: string;
    newOpeningName: string | null;

    openingPart: "both" | "active" | "inactive";
    handing: string;
    height: string;
    width: string;
    inactiveWidth: string;
    label: string;
    doorCore: string;
    doorFinish: string;
    doorGauge: string;
    doorMaterial: string;

    doorWdMaterialName: string;
    doorWdCut: string;
    doorWdMatching: string;
    doorWdMatchingMethod: string;
    doorWdPly: string;
    doorWdSwatchName: string;
    doorWdProofFile: number | null;

    doorSeries: string;
    doorThickness: string;
    doorSeam: string;
    doorLiteKitProfile?: number;
    doorLiteKitProfileCustom?: number;
    doorElevation: string;
    doorElevationFile?: { id: number; name: string };
    doorPrepMode: "simple" | "precise";
    simpleDoorPreps: string[];
    framePrepMode: "simple" | "precise";
    simpleFramePreps: string[];
    frameSeries: string;
    frameMaterial: string;
    frameGauge: string;
    frameProfile: string;
    frameConstruction: string;
    jambDepth: string;
    screenElevation?: ScreenElevationCompat;
    frameAnchorProduct?: Product;
    frameAnchorProductId?: number;
    frameAnchorOverrideQty?: number;
    customLocations?: PrepDetail[];

    degreesOfOpeningActive: number | null;
    degreesOfOpeningInactive: number | null;

    qty: number;
}

export type InventoryRequestCompat = any;